import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { 
  Package, RefreshCw, ChevronRight, LogOut, XCircle, X, 
  Download, Trash2, AlertTriangle, User as UserIcon, 
  MapPin, ShieldCheck, Save, Loader2, Calendar, Filter,
  ChevronLeft, Settings
} from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { Order, OrderDetail, formatPrice } from "../lib/api";
import { useAuthStore } from "../store/auth";
import { useCartStore } from "../store/cart";
import toast from "react-hot-toast";

const CANCEL_REASON_KEYS = [
  "changedMind",
  "mistake",
  "betterPrice",
  "tooLong",
  "other",
] as const;

type Section = "profile" | "orders" | "privacy" | "settings";

export default function AccountPage() {
  const { user, logout, setAuth } = useAuthStore();
  const { fetch: fetchCart } = useCartStore();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeSection = (searchParams.get("section") as Section) || "profile";

  // Site Mode state
  const [siteMode, setSiteMode] = useState("normal");
  const [maintenanceMessages, setMaintenanceMessages] = useState<Record<string, string>>({ en: "", ja: "" });
  const [editLang, setEditLang] = useState("en");
  const [maintenanceExpiresAt, setMaintenanceExpiresAt] = useState("");
  const [maintenanceAutoMode, setMaintenanceAutoMode] = useState("normal");
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  // Shared state
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  const [cancellingOrder, setCancellingOrder] = useState<Order | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Orders Filter/Pagination State
  const [orderStatus, setOrderStatus] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const LIMIT = 10;

  // Profile state
  const [profileName, setProfileName] = useState("");
  const [shipping, setShipping] = useState({
    address: "",
    city: "",
    state: "",
    zip: "",
    country: "Japan",
  });
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // Fetch full user data including address
  const { data: fullUser, isLoading: isUserLoading } = useQuery({
    queryKey: ["me"],
    queryFn: () => api.get("/auth/me").then((r) => r.data),
    enabled: !!user,
  });

  useEffect(() => {
    if (fullUser) {
      setProfileName(fullUser.name || "");
      setShipping({
        address: fullUser.shippingAddress || "",
        city: fullUser.shippingCity || "",
        state: fullUser.shippingState || "",
        zip: fullUser.shippingZip || "",
        country: fullUser.shippingCountry || "Japan",
      });
    }
  }, [fullUser]);

  const { data: ordersData, isLoading: isOrdersLoading } = useQuery<{ orders: Order[], total: number }>({
    queryKey: ["orders", page, orderStatus, startDate, endDate],
    queryFn: () => api.get("/orders", { 
      params: { 
        limit: LIMIT, 
        offset: (page - 1) * LIMIT,
        status: orderStatus || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined
      } 
    }).then((r) => r.data),
    enabled: !!user && activeSection === "orders",
  });

  // Fetch admin settings
  const { data: siteModeData } = useQuery({
    queryKey: ["admin-site-mode"],
    queryFn: () => api.get("/admin/settings/site-mode").then((r) => r.data),
    enabled: !!user && user.role === "admin" && activeSection === "settings",
  });

  useEffect(() => {
    if (siteModeData) {
      setSiteMode(siteModeData.siteMode);
      try {
        setMaintenanceMessages(JSON.parse(siteModeData.maintenanceMessage));
      } catch {
        setMaintenanceMessages({ en: siteModeData.maintenanceMessage, ja: siteModeData.maintenanceMessage });
      }
      setMaintenanceExpiresAt(siteModeData.maintenanceExpiresAt || "");
      setMaintenanceAutoMode(siteModeData.maintenanceAutoMode || "normal");
    }
  }, [siteModeData]);

  const handleSaveSiteMode = async () => {
    setIsSavingSettings(true);
    try {
      await api.post("/admin/settings/site-mode", { 
        siteMode, 
        maintenanceMessage: JSON.stringify(maintenanceMessages),
        maintenanceExpiresAt,
        maintenanceAutoMode
      });
      toast.success("Site settings updated!");
    } catch {
      toast.error("Failed to update site settings.");
    } finally {
      setIsSavingSettings(false);
    }
  };

  const orders = ordersData?.orders ?? [];
  const totalOrders = ordersData?.total ?? 0;
  const totalPages = Math.ceil(totalOrders / LIMIT);

  const { data: orderDetail } = useQuery<OrderDetail>({
    queryKey: ["order", expandedOrder],
    queryFn: () => api.get(`/orders/${expandedOrder}`).then((r) => r.data),
    enabled: !!expandedOrder,
  });

  async function handleSaveProfile(e: React.FormEvent) {
    e.preventDefault();
    setIsSavingProfile(true);
    try {
      // 1. Update Name
      await api.patch("/auth/me/profile", { name: profileName });
      
      // 2. Update Address
      await api.patch("/auth/me/address", {
        address: shipping.address,
        city: shipping.city,
        state: shipping.state,
        zip: shipping.zip,
        country: shipping.country,
      });

      // Update local auth store (just name)
      if (user) {
        setAuth(localStorage.getItem("artshop-token") || "", {
          ...user,
          name: profileName,
        });
      }

      queryClient.invalidateQueries({ queryKey: ["me"] });
      toast.success("Profile updated successfully");
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to update profile");
    } finally {
      setIsSavingProfile(false);
    }
  }

  async function handleExportData() {
    setIsExporting(true);
    try {
      const { data } = await api.get("/auth/me/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `user-data-${user?.id}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(t("gdpr.exportSuccess"));
    } catch {
      toast.error("Failed to export data");
    } finally {
      setIsExporting(false);
    }
  }

  async function handleDeleteAccount() {
    setIsDeleting(true);
    try {
      await api.delete("/auth/me");
      toast.success(t("gdpr.deleteSuccess"));
      logout();
      navigate("/");
    } catch {
      toast.error(t("gdpr.deleteError"));
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  }

  async function handleReorder(order: OrderDetail) {
    try {
      for (const item of order.items) {
        await api.post("/cart", { productId: item.productId, quantity: item.quantity });
      }
      await fetchCart();
      toast.success(t("account.orderPlaced"));
      navigate("/checkout");
    } catch {
      toast.error(t("account.reorderFail"));
    }
  }

  function openCancelModal(order: Order) {
    setCancellingOrder(order);
    setCancelReason("");
  }

  function closeCancelModal() {
    if (cancelling) return;
    setCancellingOrder(null);
    setCancelReason("");
  }

  async function handleConfirmCancel() {
    if (!cancellingOrder || !cancelReason) return;
    setCancelling(true);
    try {
      await api.post(`/orders/${cancellingOrder.id}/cancel`, { reason: cancelReason });
      toast.success(t("account.cancelSuccess"));
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["order", cancellingOrder.id] });
      setCancellingOrder(null);
      setCancelReason("");
    } catch {
      toast.error(t("account.cancelError"));
    } finally {
      setCancelling(false);
    }
  }

  if (!user) {
    return (
      <main className="max-w-2xl mx-auto px-4 py-16 text-center">
        <p className="text-gray-500 mb-4">{t("account.signIn")}</p>
        <Link to="/login" className="btn-primary">{t("nav.signIn")}</Link>
      </main>
    );
  }

  const sections = [
    { id: "profile", label: "Profile", icon: UserIcon },
    { id: "orders", label: "Orders", icon: Package },
    { id: "privacy", label: "Data Privacy", icon: ShieldCheck },
  ];

  if (user?.role === "admin") {
    sections.push({ id: "settings", label: "Admin Settings", icon: Settings });
  }

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex flex-col lg:flex-row gap-12">
        
        {/* Sidebar Navigation */}
        <aside className="lg:w-64 shrink-0">
          <div className="mb-8">
            <h1 className="font-display text-2xl font-bold text-gray-900">{t("account.title")}</h1>
            <p className="text-sm text-gray-500 mt-1 truncate">{user.email}</p>
          </div>

          <nav className="space-y-1">
            {sections.map((s) => (
              <button
                key={s.id}
                onClick={() => { setSearchParams({ section: s.id }); setPage(1); }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-semibold rounded-xl transition-all ${
                  activeSection === s.id
                    ? "bg-brand-50 text-brand-700 shadow-sm ring-1 ring-brand-100"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }`}
              >
                <s.icon size={18} className={activeSection === s.id ? "text-brand-600" : "text-gray-400"} />
                {s.label}
              </button>
            ))}
            
            <div className="pt-4 mt-4 border-t border-gray-100">
              <button
                onClick={() => { logout(); navigate("/"); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm font-semibold text-red-600 hover:bg-red-50 rounded-xl transition-all"
              >
                <LogOut size={18} />
                {t("nav.signOut")}
              </button>
            </div>
          </nav>
        </aside>

        {/* Content Area */}
        <div className="flex-1 min-w-0">
          
          {/* PROFILE SECTION */}
          {activeSection === "profile" && (
            <section className="animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="flex items-center gap-2 mb-8">
                <div className="w-10 h-10 rounded-full bg-brand-50 flex items-center justify-center text-brand-600 border border-brand-100">
                  <UserIcon size={20} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Personal Information</h2>
                  <p className="text-xs text-gray-500">Update your profile details and shipping address</p>
                </div>
              </div>

              {isUserLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-brand-600 animate-spin" />
                </div>
              ) : (
                <form onSubmit={handleSaveProfile} className="space-y-8 bg-white border border-gray-100 rounded-3xl p-6 sm:p-8 shadow-sm">
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Display Name</label>
                      <input
                        type="text"
                        required
                        className="input"
                        value={profileName}
                        onChange={(e) => setProfileName(e.target.value)}
                        placeholder="Your full name"
                      />
                    </div>
                  </div>

                  <div className="pt-6 border-t border-gray-50">
                    <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <MapPin size={16} className="text-gray-400" />
                      Default Shipping Address
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="sm:col-span-2">
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Street Address</label>
                        <input
                          type="text"
                          className="input text-sm"
                          value={shipping.address}
                          onChange={(e) => setShipping({ ...shipping, address: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">City</label>
                        <input
                          type="text"
                          className="input text-sm"
                          value={shipping.city}
                          onChange={(e) => setShipping({ ...shipping, city: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">State / Prefecture</label>
                        <input
                          type="text"
                          className="input text-sm"
                          value={shipping.state}
                          onChange={(e) => setShipping({ ...shipping, state: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Postal Code</label>
                        <input
                          type="text"
                          className="input text-sm"
                          value={shipping.zip}
                          onChange={(e) => setShipping({ ...shipping, zip: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Country</label>
                        <input
                          type="text"
                          className="input text-sm"
                          value={shipping.country}
                          onChange={(e) => setShipping({ ...shipping, country: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 flex justify-end">
                    <button
                      type="submit"
                      disabled={isSavingProfile}
                      className="btn-primary flex items-center gap-2 px-8"
                    >
                      {isSavingProfile ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                      Save Changes
                    </button>
                  </div>
                </form>
              )}
            </section>
          )}

          {/* ORDERS SECTION */}
          {activeSection === "orders" && (
            <section className="animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-brand-50 flex items-center justify-center text-brand-600 border border-brand-100">
                    <Package size={20} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{t("account.orderHistory")}</h2>
                    <p className="text-xs text-gray-500">View and track your previous purchases</p>
                  </div>
                </div>
              </div>

              {/* Filter Bar */}
              <div className="bg-white border border-gray-100 rounded-2xl p-4 mb-6 shadow-sm flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-widest min-w-fit">
                  <Filter size={14} /> Filters
                </div>
                
                <div className="flex flex-1 flex-wrap gap-3">
                  <select 
                    className="input !py-1.5 !text-xs min-w-[120px]"
                    value={orderStatus}
                    onChange={(e) => { setOrderStatus(e.target.value); setPage(1); }}
                  >
                    <option value="">All Statuses</option>
                    <option value="pending">{t("statuses.pending")}</option>
                    <option value="paid">{t("statuses.paid")}</option>
                    <option value="shipped">{t("statuses.shipped")}</option>
                    <option value="delivered">{t("statuses.delivered")}</option>
                    <option value="cancelled">{t("statuses.cancelled")}</option>
                  </select>

                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input 
                        type="date" 
                        className="input !py-1.5 !pl-9 !text-xs"
                        value={startDate}
                        onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                      />
                    </div>
                    <span className="text-gray-300">—</span>
                    <div className="relative">
                      <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input 
                        type="date" 
                        className="input !py-1.5 !pl-9 !text-xs"
                        value={endDate}
                        onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                      />
                    </div>
                  </div>

                  {(orderStatus || startDate || endDate) && (
                    <button 
                      onClick={() => { setOrderStatus(""); setStartDate(""); setEndDate(""); setPage(1); }}
                      className="text-xs font-bold text-brand-600 hover:text-brand-700 transition-colors px-2"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              {isOrdersLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-24 rounded-2xl bg-gray-50 animate-pulse" />
                  ))}
                </div>
              ) : orders.length === 0 ? (
                <div className="rounded-3xl border-2 border-dashed border-gray-100 p-16 text-center bg-gray-50/30">
                  <Package size={48} className="text-gray-200 mx-auto mb-4" />
                  <p className="text-gray-500 mb-6">{t("account.noOrders")}</p>
                  <Link to="/shop" className="btn-primary">{t("account.startShopping")}</Link>
                </div>
              ) : (
                <>
                  <ul className="space-y-4 mb-8">
                    {orders.map((order) => (
                      <li key={order.id} className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden transition-all hover:border-brand-200">
                        <button
                          className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50/50 transition-colors"
                          onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                        >
                          <div className="flex flex-col gap-1">
                            <span className="text-sm font-bold text-gray-900">
                              {t("account.order", { id: order.id.slice(0, 8).toUpperCase() })}
                            </span>
                            <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">
                              {new Date(order.createdAt).toLocaleDateString(undefined, {
                                year: "numeric", month: "long", day: "numeric",
                              })}
                            </span>
                          </div>
                          <div className="flex items-center gap-6">
                            <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border ${statusColor(order.status)}`}>
                              {t(`statuses.${order.status}`)}
                            </span>
                            <span className="font-bold text-gray-900">{formatPrice(order.total, order.currency ?? "USD")}</span>
                            <ChevronRight
                              size={18}
                              className={`text-gray-300 transition-transform duration-300 ${expandedOrder === order.id ? "rotate-90" : ""}`}
                            />
                          </div>
                        </button>

                        {expandedOrder === order.id && orderDetail?.id === order.id && (
                          <div className="border-t border-gray-100 p-6 space-y-6 bg-gray-50/30 animate-in slide-in-from-top-2 duration-200">
                            <ul className="space-y-4">
                              {orderDetail.items.map((item) => (
                                <li key={item.id} className="flex items-center gap-4">
                                  <img
                                    src={item.productImageUrl || "/placeholder.jpg"}
                                    alt={item.productName}
                                    className="h-16 w-16 rounded-xl object-cover shadow-sm border border-white"
                                  />
                                  <div className="flex-1 text-sm">
                                    <p className="font-bold text-gray-900">{item.productName}</p>
                                    <p className="text-gray-500 font-medium">
                                      {t("checkout.qty", { count: item.quantity })} · {formatPrice(item.price, orderDetail.currency ?? "USD")}
                                    </p>
                                  </div>
                                </li>
                              ))}
                            </ul>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-6 border-t border-gray-100">
                              <div className="space-y-3">
                                <div className="border-b border-gray-100 pb-2">
                                  <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Order Summary</h4>
                                </div>
                                <div className="space-y-1.5 text-sm">
                                  {orderDetail.taxAmount ? (
                                    <>
                                      <div className="flex justify-between text-gray-500 font-medium">
                                        <span>{t("account.subtotal")}</span>
                                        <span>{formatPrice(orderDetail.total - orderDetail.taxAmount, orderDetail.currency ?? "USD")}</span>
                                      </div>
                                      <div className="flex justify-between text-gray-500 font-medium">
                                        <span>{t("account.tax")}</span>
                                        <span>{formatPrice(orderDetail.taxAmount, orderDetail.currency ?? "USD")}</span>
                                      </div>
                                    </>
                                  ) : null}
                                  <div className="flex justify-between font-bold text-gray-900 text-lg pt-1">
                                    <span>{t("checkout.total")}</span>
                                    <span>{formatPrice(orderDetail.total, orderDetail.currency ?? "USD")}</span>
                                  </div>
                                </div>
                              </div>

                              <div className="space-y-3 text-sm">
                                <div className="border-b border-gray-100 pb-2">
                                  <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Shipping Details</h4>
                                </div>
                                {orderDetail.shippingName && (
                                  <div className="text-gray-600 font-medium leading-relaxed">
                                    <p className="text-gray-900 font-bold mb-1">{orderDetail.shippingName}</p>
                                    <p>{orderDetail.shippingAddress}</p>
                                    <p>{orderDetail.shippingCity}, {orderDetail.shippingState} {orderDetail.shippingZip}</p>
                                    <p>{orderDetail.shippingCountry}</p>
                                  </div>
                                )}
                              </div>
                            </div>

                            {orderDetail.cancelReason && (
                              <div className="p-4 rounded-xl bg-red-50 border border-red-100 flex items-start gap-3">
                                <AlertTriangle className="text-red-500 shrink-0 mt-0.5" size={16} />
                                <p className="text-xs text-red-700 font-medium">
                                  {t("account.cancelledReason", { reason: orderDetail.cancelReason })}
                                </p>
                              </div>
                            )}

                            <div className="flex items-center gap-3 flex-wrap pt-2">
                              {["pending", "paid"].includes(orderDetail.status) && (
                                <button
                                  onClick={() => openCancelModal(orderDetail)}
                                  className="flex items-center gap-2 text-xs font-bold text-red-600 hover:text-red-800 border border-red-200 hover:border-red-400 rounded-xl px-5 py-2.5 transition-all bg-white"
                                >
                                  <XCircle size={14} /> {t("account.cancelOrder")}
                                </button>
                              )}
                              <button
                                onClick={() => handleReorder(orderDetail)}
                                className="btn-secondary flex items-center gap-2 text-xs font-bold px-5 py-2.5"
                              >
                                <RefreshCw size={14} /> {t("account.reorder")}
                              </button>
                            </div>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>

                  {/* Paginator */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between border-t border-gray-100 pt-6">
                      <p className="text-xs text-gray-500 font-medium">
                        Showing {(page - 1) * LIMIT + 1} to {Math.min(page * LIMIT, totalOrders)} of {totalOrders} orders
                      </p>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setPage(p => Math.max(1, p - 1))}
                          disabled={page === 1}
                          className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                        >
                          <ChevronLeft size={18} />
                        </button>
                        <div className="flex items-center gap-1">
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            // Show pages around current page
                            let p = page - 2 + i;
                            if (page <= 2) p = i + 1;
                            if (page >= totalPages - 1) p = totalPages - 4 + i;
                            if (p < 1 || p > totalPages) return null;
                            return (
                              <button
                                key={p}
                                onClick={() => setPage(p)}
                                className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${
                                  page === p 
                                    ? "bg-brand-600 text-white shadow-md shadow-brand-200" 
                                    : "text-gray-500 hover:bg-gray-100"
                                }`}
                              >
                                {p}
                              </button>
                            );
                          })}
                        </div>
                        <button
                          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                          disabled={page === totalPages}
                          className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                        >
                          <ChevronRight size={18} />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </section>
          )}

          {/* PRIVACY SECTION */}
          {activeSection === "privacy" && (
            <section className="animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="flex items-center gap-2 mb-8">
                <div className="w-10 h-10 rounded-full bg-brand-50 flex items-center justify-center text-brand-600 border border-brand-100">
                  <ShieldCheck size={20} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Data Privacy</h2>
                  <p className="text-xs text-gray-500">Manage your personal data and privacy settings</p>
                </div>
              </div>

              <div className="bg-white border border-gray-100 rounded-3xl p-6 sm:p-10 shadow-sm space-y-10">
                <div>
                  <h3 className="text-sm font-bold text-gray-900 mb-4">Export Personal Data</h3>
                  <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                    You have the right to receive a copy of all your personal data stored on our platform, 
                    including your account details, order history, and preferences.
                  </p>
                  <button
                    onClick={handleExportData}
                    disabled={isExporting}
                    className="flex items-center gap-2 text-sm font-bold text-gray-700 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 px-6 py-3 rounded-xl transition-all"
                  >
                    {isExporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                    {isExporting ? "Preparing Export..." : t("gdpr.exportData")}
                  </button>
                </div>

                <div className="pt-10 border-t border-gray-50">
                  <h3 className="text-sm font-bold text-red-600 mb-4">Delete Account</h3>
                  <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                    Once you delete your account, there is no going back. All your personal data, 
                    order history, and saved items will be permanently erased.
                  </p>
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="flex items-center gap-2 text-sm font-bold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-6 py-3 rounded-xl transition-all"
                  >
                    <Trash2 size={16} />
                    {t("gdpr.deleteAccount")}
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* ADMIN SETTINGS SECTION */}
          {activeSection === "settings" && user?.role === "admin" && (
            <section className="animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="flex items-center gap-2 mb-8">
                <div className="w-10 h-10 rounded-full bg-brand-50 flex items-center justify-center text-brand-600 border border-brand-100">
                  <Settings size={20} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Admin Settings</h2>
                  <p className="text-xs text-gray-500">Manage global site operational modes</p>
                </div>
              </div>

              <div className="bg-white border border-gray-100 rounded-3xl p-6 sm:p-10 shadow-sm space-y-10">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-widest">Master Switch</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {[
                        { id: "normal", label: "Normal", desc: "Full functionality" },
                        { id: "read_only", label: "Read-Only", desc: "Browsing only, no orders" },
                        { id: "maintenance", label: "Maintenance", desc: "Logo & message only" },
                      ].map((mode) => (
                        <button
                          key={mode.id}
                          onClick={() => setSiteMode(mode.id)}
                          className={`flex flex-col items-start p-4 rounded-2xl border-2 transition-all text-left ${
                            siteMode === mode.id
                              ? "border-brand-500 bg-brand-50/50"
                              : "border-gray-100 hover:border-gray-200 hover:bg-gray-50/50"
                          }`}
                        >
                          <span className={`text-sm font-bold ${siteMode === mode.id ? "text-brand-700" : "text-gray-900"}`}>{mode.label}</span>
                          <span className="text-[10px] text-gray-500 font-medium">{mode.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {siteMode !== "normal" && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-top-2 duration-300">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest">Maintenance Message</label>
                          <select 
                            className="text-[10px] font-bold text-brand-600 uppercase tracking-widest bg-brand-50 border-none rounded-lg py-1 px-2 cursor-pointer outline-none"
                            value={editLang}
                            onChange={(e) => setEditLang(e.target.value)}
                          >
                            <option value="en">English</option>
                            <option value="ja">日本語 (Japanese)</option>
                          </select>
                        </div>
                        <textarea
                          className="input min-h-[100px] py-3 text-sm leading-relaxed"
                          value={maintenanceMessages[editLang] || ""}
                          onChange={(e) => setMaintenanceMessages({ ...maintenanceMessages, [editLang]: e.target.value })}
                          placeholder={`Enter ${editLang === 'en' ? 'English' : 'Japanese'} message...`}
                        />
                      </div>

                      {siteMode === "maintenance" && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4 border-t border-gray-50">
                          <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Expiration Date/Time</label>
                            <input
                              type="datetime-local"
                              className="input"
                              value={maintenanceExpiresAt}
                              onChange={(e) => setMaintenanceExpiresAt(e.target.value)}
                            />
                            <p className="text-[9px] text-gray-400 mt-1 italic">Optional. System will auto-switch mode when reached.</p>
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Auto-Switch To</label>
                            <select
                              className="input"
                              value={maintenanceAutoMode}
                              onChange={(e) => setMaintenanceAutoMode(e.target.value)}
                            >
                              <option value="normal">Normal (Open Store)</option>
                              <option value="read_only">Read-Only (View Only)</option>
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="pt-6 border-t border-gray-50 flex justify-end">
                    <button
                      onClick={handleSaveSiteMode}
                      disabled={isSavingSettings}
                      className="btn-primary flex items-center gap-2 px-8"
                    >
                      {isSavingSettings ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                      Update Site Mode
                    </button>
                  </div>
                </div>
              </div>
            </section>
          )}

        </div>
      </div>

      {/* Modals remain same but styled */}
      {cancellingOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="font-bold text-gray-900">
                {t("account.cancelTitle", { id: cancellingOrder.id.slice(0, 8).toUpperCase() })}
              </h2>
              <button onClick={closeCancelModal} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600 font-medium">{t("account.cancelQuestion")}</p>
              <div className="space-y-2">
                {CANCEL_REASON_KEYS.map((key) => {
                  const label = t(`account.cancelReasons.${key}`);
                  return (
                    <label
                      key={key}
                      className={`flex items-center gap-3 p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                        cancelReason === label
                          ? "border-brand-500 bg-brand-50/50"
                          : "border-gray-100 hover:border-gray-200 hover:bg-gray-50/50"
                      }`}
                    >
                      <input
                        type="radio"
                        name="cancelReason"
                        value={label}
                        checked={cancelReason === label}
                        onChange={() => setCancelReason(label)}
                        className="accent-brand-600 w-4 h-4"
                      />
                      <span className="text-sm font-bold text-gray-800">{label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-3 p-6 bg-gray-50 border-t border-gray-100">
              <button onClick={closeCancelModal} disabled={cancelling} className="flex-1 btn-secondary text-sm font-bold">
                {t("account.cancelClose")}
              </button>
              <button
                onClick={handleConfirmCancel}
                disabled={!cancelReason || cancelling}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-bold py-3 px-4 rounded-2xl transition-all shadow-md hover:shadow-lg"
              >
                {cancelling ? <Loader2 size={18} className="animate-spin mx-auto" /> : t("account.cancelConfirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertTriangle size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{t("gdpr.deleteConfirmTitle")}</h3>
              <p className="text-sm text-gray-500 leading-relaxed font-medium">
                {t("gdpr.deleteConfirmText")}
              </p>
            </div>
            <div className="flex gap-3 p-6 bg-gray-50 border-t border-gray-100">
              <button onClick={() => setShowDeleteModal(false)} disabled={isDeleting} className="flex-1 btn-secondary text-sm font-bold">
                {t("account.cancelClose")}
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={isDeleting}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-bold py-3 px-4 rounded-2xl transition-all shadow-md hover:shadow-lg"
              >
                {isDeleting ? <Loader2 size={18} className="animate-spin mx-auto" /> : t("gdpr.deleteAccount")}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

function statusColor(status: string) {
  const map: Record<string, string> = {
    pending: "bg-yellow-50 text-yellow-700 border-yellow-100",
    paid: "bg-green-50 text-green-700 border-green-100",
    shipped: "bg-blue-50 text-blue-700 border-blue-100",
    delivered: "bg-brand-50 text-brand-700 border-brand-100",
    cancelled: "bg-red-50 text-red-700 border-red-100",
  };
  return map[status] ?? "bg-gray-100 text-gray-600 border-gray-200";
}
